// src/pages/Accounting/ChartOfAccounts/AccountDrillPage.tsx
//
// Account drill-in page shell.
// Route: /accounting/chart-of-accounts/:accountId
// Page header is exported as ChartOfAccountsDrillHeader and rendered via
// AppLayout's pageHeader prop (wired in src/routes/authedRoutes.tsx)
// so it stays pinned when the drill content scrolls.

import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Download } from "lucide-react";
import DrillView from "./components/DrillView";
import { PALETTE } from "./constants";
import { MOCK_ACCOUNTS, MOCK_RECEIPTS, MOCK_TRANSACTIONS } from "./mockData";

/**
 * Parses accountId from pathname. useParams cannot be used here because the
 * header is rendered outside the <Routes> element (inside LayoutShell).
 */
function useAccountIdFromPath(): string | undefined {
  const location = useLocation();
  const match = location.pathname.match(
    /^\/accounting\/chart-of-accounts\/(.+)$/
  );
  return match?.[1];
}

/**
 * Sticky page header for /accounting/chart-of-accounts/:accountId.
 * Looks the account up from mock data; renders a Not-found header if unknown.
 */
export function ChartOfAccountsDrillHeader() {
  const navigate = useNavigate();
  const accountId = useAccountIdFromPath();
  const account = accountId ? MOCK_ACCOUNTS[accountId] : null;
  const transactions = accountId ? MOCK_TRANSACTIONS[accountId] || [] : [];

  return (
    <div style={headerStyles.wrap}>
      <div style={headerStyles.crumb}>
        <span
          style={headerStyles.crumbLink}
          onClick={() => navigate("/accounting")}
        >
          Accounting
        </span>
        <span style={headerStyles.crumbSep}>/</span>
        <span
          style={headerStyles.crumbLink}
          onClick={() => navigate("/accounting/chart-of-accounts")}
        >
          Chart of accounts
        </span>
        <span style={headerStyles.crumbSep}>/</span>
        <span>{account ? account.name : "Not found"}</span>
      </div>
      <h1 style={headerStyles.title}>
        {account ? account.name : "Account not found"}
      </h1>
      <div style={headerStyles.sub}>
        {account
          ? `Account ${account.number} · ${account.type} · ${transactions.length} transactions this month`
          : "This account does not exist or you do not have access."}
      </div>
    </div>
  );
}

const headerStyles: Record<string, React.CSSProperties> = {
  wrap: { padding: "14px 32px 14px" },
  crumb: {
    fontSize: 13,
    color: PALETTE.ink3,
    marginBottom: 4,
    fontWeight: 500,
  },
  crumbLink: { color: PALETTE.ink3, cursor: "pointer" },
  crumbSep: { color: PALETTE.ink3, margin: "0 6px" },
  title: {
    fontSize: 32,
    fontWeight: 800,
    margin: 0,
    letterSpacing: "-0.02em",
    color: PALETTE.ink,
  },
  sub: {
    fontSize: 13.5,
    color: PALETTE.ink3,
    marginTop: 6,
    fontWeight: 500,
  },
};

export default function AccountDrillPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();

  const account = accountId ? MOCK_ACCOUNTS[accountId] : null;
  const transactions = accountId ? MOCK_TRANSACTIONS[accountId] || [] : [];
  const receipts = accountId ? MOCK_RECEIPTS[accountId] || [] : [];

  if (!account) {
    return (
      <div style={styles.page}>
        <div style={{ padding: "40px 0" }}>
          <button
            style={{ ...styles.btn, ...styles.btnPrimary }}
            onClick={() => navigate("/accounting/chart-of-accounts")}
          >
            Back to Chart of accounts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.actionBar}>
        <button style={styles.btn}>
          <Download size={14} /> All receipts
        </button>
        <button style={styles.btn}>Export CSV</button>
        <button style={{ ...styles.btn, ...styles.btnPrimary }}>
          + New transaction
        </button>
      </div>

      <DrillView
        account={account}
        transactions={transactions}
        receipts={receipts}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: "100%",
    margin: 0,
    marginTop: -28,
    padding: "20px 32px 90px",
    background: "#fff",
    minHeight: "100vh",
  },
  actionBar: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    marginBottom: 20,
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 36,
    padding: "0 16px",
    borderRadius: 9,
    fontSize: 13,
    fontWeight: 700,
    border: `1px solid ${PALETTE.line}`,
    background: "#fff",
    color: PALETTE.ink,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnPrimary: {
    background: PALETTE.tealDark,
    color: "#fff",
    borderColor: PALETTE.tealDark,
  },
};